import { Button, Card, Dialog, Flex, Text } from '@applyboard/crystal-ui'

import { convertTimelessDateStrToLocalDate } from '../../../utils/convertTimelessDateStrToLocalDate'
import { StudentApplication } from '../../../components/Application/types'
import { IntakeSearchResource } from '@backend/resources/intake-search/types'
import { useState } from 'react'
import { format } from 'date-fns'

type ChangeProgramDialogProps = Readonly<{
  currentApplication: StudentApplication['attributes']
  onConfirm: () => void
  emphasis: 'highlighted' | 'outlined'
  loading?: boolean
  disabled?: boolean
  selectedIntake: IntakeSearchResource
}>

export function ChangeProgramDialog(props: ChangeProgramDialogProps) {
  const [open, setOpen] = useState(false)
  const {
    onConfirm,
    loading,
    disabled,
    emphasis,
    currentApplication,
    selectedIntake: intakeSearch,
  } = props

  const applicationSummary = {
    programName: currentApplication?.programSelected?.program?.name ?? '',
    intakeTermDate: currentApplication?.programSelected?.programIntakeTerm?.startDate
      ? format(
          convertTimelessDateStrToLocalDate(
            currentApplication.programSelected?.programIntakeTerm?.startDate,
          ),
          'MMM yyyy',
        )
      : '',
    campusName: currentApplication?.programSelected?.campus?.name ?? '',
  }

  return (
    <Dialog size="md" open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button intent="primary" emphasis={emphasis} width="fill" disabled={disabled}>
          Apply
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>Confirm your update</Dialog.Heading>
      <Dialog.Content aria-describedby="dialog-description">
        <Flex gap={4} direction="column">
          <Text as="p" id="dialog-description">
            This action will do the following update to your application:
          </Text>
          <Card variant="highlighted" aria-label="Change program information">
            <section aria-label="Current program information">
              <Flex gap={1} direction="column">
                <Text intent="primary" variant="bodyM" contrast="mid">
                  <strong>{`Current:`}</strong>
                </Text>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Program:</strong>
                  </Text>
                  <Text variant="bodyM" data-testid="program-name">
                    {applicationSummary.programName}
                  </Text>
                </Flex>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Intake:</strong>
                  </Text>
                  <Text variant="bodyM">{applicationSummary.intakeTermDate}</Text>
                </Flex>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Campus:</strong>
                  </Text>
                  <Text variant="bodyM" data-testid="campus-name">
                    {applicationSummary.campusName}
                  </Text>
                </Flex>
              </Flex>
            </section>
            <section aria-label="New program information">
              <Flex gap={1} direction="column" pt={4}>
                <Text intent="primary" variant="bodyM" contrast="mid">
                  <strong>{`New:`}</strong>
                </Text>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Program:</strong>
                  </Text>
                  <Text variant="bodyM">{intakeSearch.attributes.program.name}</Text>
                </Flex>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Intake:</strong>
                  </Text>
                  <Text variant="bodyM">
                    {format(
                      convertTimelessDateStrToLocalDate(
                        intakeSearch.attributes.intakeTerm.startsOn,
                      ),
                      'MMM yyyy',
                    )}
                  </Text>
                </Flex>
                <Flex gap={1}>
                  <Text variant="bodyM">
                    <strong>Campus:</strong>
                  </Text>
                  <Text variant="bodyM">{intakeSearch.attributes.campus.name}</Text>
                </Flex>
              </Flex>
            </section>
          </Card>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton disabled={loading} intent="secondary" width={'fill'}>
          Cancel
        </Dialog.CloseButton>
        <Button intent="primary" onClick={onConfirm} width={'fill'} loading={loading}>
          Confirm & update
        </Button>
      </Dialog.Actions>
    </Dialog>
  )
}
